import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, X, Loader2, Share2, Sparkles, Search, Check } from "lucide-react";
import api, { inr, resolveImg, formatApiErrorDetail } from "../lib/api";

export default function BuildMyLook() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState("");
  const [name, setName] = useState("");
  const [room, setRoom] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/products").then(r => {
      setProducts(r.data.filter(p => p.in_stock !== false));
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const toggleProduct = useCallback((product) => {
    setSelected(prev => {
      const exists = prev.find(p => p.id === product.id);
      if (exists) return prev.filter(p => p.id !== product.id);
      if (prev.length >= 12) {
        toast.error("Maximum 12 products per look");
        return prev;
      }
      return [...prev, { ...product, note: "" }];
    });
  }, []);

  const updateNote = (productId, note) => {
    setSelected(prev => prev.map(p => p.id === productId ? { ...p, note } : p));
  };

  const removeFromLook = (productId) => {
    setSelected(prev => prev.filter(p => p.id !== productId));
  };

  const saveLook = async () => {
    if (!name.trim()) return toast.error("Give your look a name");
    if (selected.length === 0) return toast.error("Add at least one product");
    setSaving(true);
    try {
      const { data } = await api.post("/looks", {
        name: name.trim(),
        description: description.trim(),
        room: room.trim(),
        items: selected.map(p => ({ product_id: p.id, note: p.note || "" })),
      });
      toast.success("Look created! Redirecting to your shareable page...");
      navigate(`/look/${data.share_id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const filtered = search.trim()
    ? products.filter(p => p.name.toLowerCase().includes(search.toLowerCase()) || p.category.toLowerCase().includes(search.toLowerCase()))
    : products;

  const total = selected.reduce((sum, p) => sum + p.price, 0);
  const isSelected = (id) => selected.some(p => p.id === id);

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12" data-testid="build-my-look-page">
      <div className="text-center mb-10">
        <p className="text-xs uppercase tracking-[0.25em] text-accent font-semibold">Curate Your Space</p>
        <h1 className="mt-3 text-4xl sm:text-5xl font-display text-primary">Build My Look</h1>
        <p className="mt-4 text-muted-foreground max-w-lg mx-auto">
          Pick your favourite Aethel Decor pieces, arrange them into a room look, and share it with anyone.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Product Browser */}
        <div className="lg:col-span-2">
          <div className="flex items-center gap-3 mb-5">
            <div className="flex-1 relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search products..."
                className="w-full bg-muted pl-10 pr-4 py-2.5 text-sm outline-none focus:ring-1 focus:ring-primary"
                data-testid="look-search"
              />
            </div>
            <span className="text-xs text-muted-foreground whitespace-nowrap">{selected.length} selected</span>
          </div>

          {loading ? (
            <div className="py-16 text-center"><Loader2 size={24} className="animate-spin mx-auto text-primary" /></div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {filtered.map(product => {
                const active = isSelected(product.id);
                return (
                  <button
                    key={product.id}
                    onClick={() => toggleProduct(product)}
                    className={`relative group text-left border transition-all ${
                      active ? "border-primary ring-2 ring-primary/20" : "border-border hover:border-primary/40"
                    }`}
                    data-testid={`look-product-${product.id}`}
                  >
                    <div className="aspect-[4/5] bg-muted overflow-hidden">
                      <img
                        src={resolveImg(product.images?.[0])}
                        alt={product.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-2.5">
                      <p className="text-xs line-clamp-2 leading-tight">{product.name}</p>
                      <p className="text-xs font-semibold text-primary mt-1">{inr(product.price)}</p>
                    </div>
                    {active && (
                      <div className="absolute top-2 right-2 w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                        <Check size={14} className="text-primary-foreground" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Look Builder Sidebar */}
        <div className="lg:col-span-1">
          <div className="lg:sticky lg:top-28 space-y-5">
            <div className="border border-border bg-card p-5">
              <h2 className="font-display text-lg text-primary mb-4 flex items-center gap-2">
                <Sparkles size={18} className="text-accent" /> Your Look
              </h2>

              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Name your look *"
                className="w-full bg-muted px-3 py-2.5 text-sm outline-none focus:ring-1 focus:ring-primary mb-3"
                data-testid="look-name"
              />
              <div className="grid grid-cols-2 gap-3 mb-3">
                <select
                  value={room}
                  onChange={e => setRoom(e.target.value)}
                  className="bg-muted px-3 py-2.5 text-sm outline-none"
                  data-testid="look-room"
                >
                  <option value="">Room (optional)</option>
                  <option value="Living Room">Living Room</option>
                  <option value="Bedroom">Bedroom</option>
                  <option value="Dining Area">Dining Area</option>
                  <option value="Kitchen">Kitchen</option>
                  <option value="Whole Home">Whole Home</option>
                </select>
                <input
                  type="text"
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  placeholder="Short description"
                  className="bg-muted px-3 py-2.5 text-sm outline-none"
                  data-testid="look-desc"
                />
              </div>

              {/* Selected items */}
              {selected.length === 0 ? (
                <div className="py-8 text-center text-muted-foreground text-sm">
                  <Plus size={24} className="mx-auto mb-2 opacity-40" />
                  <p>Click products to add them to your look</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
                  {selected.map(item => (
                    <div key={item.id} className="flex items-center gap-2.5 bg-muted/50 p-2 group" data-testid={`look-item-${item.id}`}>
                      <div className="w-10 h-12 bg-muted overflow-hidden shrink-0">
                        <img src={resolveImg(item.images?.[0])} alt="" className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs line-clamp-1">{item.name}</p>
                        <p className="text-xs text-primary font-semibold">{inr(item.price)}</p>
                      </div>
                      <button
                        onClick={() => removeFromLook(item.id)}
                        className="text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity p-1"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Total + Save */}
              {selected.length > 0 && (
                <div className="border-t border-border mt-4 pt-4">
                  <div className="flex justify-between text-sm mb-4">
                    <span className="text-muted-foreground">{selected.length} item{selected.length > 1 ? "s" : ""}</span>
                    <span className="font-semibold text-primary">{inr(total)}</span>
                  </div>
                  <button
                    onClick={saveLook}
                    disabled={saving || !name.trim()}
                    className="w-full bg-primary text-primary-foreground py-3 text-sm flex items-center justify-center gap-2 hover:bg-primary/90 transition-colors disabled:opacity-50"
                    data-testid="look-save-btn"
                  >
                    {saving ? <Loader2 size={16} className="animate-spin" /> : <Share2 size={16} />}
                    {saving ? "Creating..." : "Create & Share My Look"}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
